import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, FileText, ShieldCheck } from "@phosphor-icons/react";
import { getPolicyLinks, getPolicyBySlug, supportEmail } from "@/content/policies";
import { useLanguage } from "@/i18n/LanguageContext";
import { getT } from "@/i18n/translations";

const backLabel = { en: "Back to 1:2000 Club", sk: "Sp\u00E4\u0165 na 1:2000 Club", de: "Zur\u00FCck zum 1:2000 Club" };
const lastUpdatedLabel = { en: "Last updated", sk: "Posledn\u00E1 aktualiz\u00E1cia", de: "Zuletzt aktualisiert" };

const PolicyPage = () => {
  const { slug } = useParams();
  const { language } = useLanguage();
  const policy = getPolicyBySlug(slug, language);
  const links = getPolicyLinks(language);

  if (!policy) {
    return <Navigate to="/legal/terms" replace />;
  }

  return (
    <div className="min-h-screen bg-[#070501] text-[#cba135]">
      <main className="mx-auto flex min-h-screen w-full max-w-5xl flex-col px-5 py-6 md:px-8 md:py-10">
        <header className="mb-10 flex flex-col gap-5 border-b border-[#cba135]/10 pb-6 md:flex-row md:items-center md:justify-between">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm font-semibold text-[#cba135]/80 transition-colors hover:text-[#cba135]"
          >
            <ArrowLeft size={18} weight="bold" />
            {backLabel[language] || backLabel.en}
          </Link>

          <nav className="flex flex-wrap gap-2" aria-label="Legal documents">
            {links.map((link) => (
              <Link
                key={link.slug}
                to={`/legal/${link.slug}`}
                className={`rounded-md border px-3 py-2 text-xs font-semibold transition-colors ${
                  link.slug === slug
                    ? "border-[#cba135] bg-[#cba135] text-[#070501]"
                    : "border-[#cba135]/15 bg-[#0e0b04] text-[#cba135]/70 hover:border-[#cba135]/40 hover:text-[#cba135]"
                }`}
              >
                {link.title}
              </Link>
            ))}
          </nav>
        </header>

        <section className="mb-10">
          <div className="mb-4 inline-flex items-center gap-2 rounded-md border border-[#cba135]/15 bg-[#0e0b04] px-3 py-2 text-xs font-semibold uppercase text-[#cba135]/70">
            <ShieldCheck size={16} weight="duotone" />
            {policy.eyebrow}
          </div>
          <h1 className="heading-font text-4xl font-bold text-[#cba135] md:text-6xl">
            {policy.title}
          </h1>
          <p className="mt-4 max-w-3xl text-base leading-relaxed text-[#cba135]/70 md:text-lg">
            {policy.intro}
          </p>
          <p className="mt-3 text-sm text-[#cba135]/50">
            {lastUpdatedLabel[language] || lastUpdatedLabel.en}: {policy.lastUpdated}
          </p>
        </section>

        <article className="space-y-5">
          {policy.sections.map((section) => (
            <section
              key={section.heading}
              className="rounded-lg border border-[#cba135]/10 bg-[#0e0b04] p-5 md:p-6"
            >
              <div className="mb-3 flex items-center gap-2">
                <FileText size={18} weight="duotone" className="text-[#cba135]/80" />
                <h2 className="text-xl font-semibold text-[#cba135]">{section.heading}</h2>
              </div>
              <div className="space-y-3 text-sm leading-relaxed text-[#cba135]/70 md:text-base">
                {section.body.map((paragraph, i) => (
                  <p key={`${section.heading || 'sec'}-${i}`}>{paragraph}</p>
                ))}
              </div>
            </section>
          ))}
        </article>

        <footer className="mt-12 border-t border-[#cba135]/10 py-6 text-sm text-[#cba135]/55">
          <p>
            Support:{" "}
            <a className="text-[#cba135] underline underline-offset-4" href={`mailto:${supportEmail}`}>
              {supportEmail}
            </a>
          </p>
        </footer>
      </main>
    </div>
  );
};

export default PolicyPage;
